import {
  AfterViewInit,
  Component,
  ComponentRef,
  ViewContainerRef,
  inject,
  inputBinding,
  signal,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Analytics } from '@core/services/analytics';
import { ApiClient } from '@core/services/api-client';
import { Button } from '@shared/components/button/button';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { CheckEmailResponse, InviteData } from '@shared/models/invitation';

@Component({
  selector: 'app-exam-confirm-page',
  imports: [Button],
  templateUrl: './exam-confirm-page.html',
  styles: [
    `
      .floating-shape-1 {
        animation-duration: 6s;
      }
      .floating-shape-2 {
        animation-duration: 25s;
      }
      .floating-shape-3 {
        animation-duration: 20s;
      }
      .floating-shape-4 {
        animation-duration: 18s;
      }
      @keyframes gradientSlide {
        0% {
          background-position: 0% 0%;
        }
        50% {
          background-position: 100% 100%;
        }
        100% {
          background-position: 0% 0%;
        }
      }
    `,
  ],
})
export class ExamConfirmPage implements AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private apiClient = inject(ApiClient);
  private analytics = inject(Analytics);

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  private urlToken = this.route.snapshot.queryParamMap.get('confirm-token');

  readonly disabled = signal<boolean>(false);

  // Toast functionality
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  async loginWithGithub() {
    try {
      this.disabled.set(true);
      localStorage.removeItem('eachProblemResult');
      await this.apiClient.getGithubLoginUrl();
      this.enableButton();
    } catch (error) {
      console.error('Github login error:', error);
      this.showToast('error', 'GitHub login failed. Please try again.');
      this.enableButton();
    }
  }

  ngAfterViewInit() {
    this.analytics.configureUserTracking();
    const storedInvite = this.getStoredInviteData();
    const token = this.urlToken ?? storedInvite?.invitedToken;

    if (!token) {
      this.router.navigate(['/forbidden']);
      return;
    }

    // If valid invite data with a testSetId already exists, redirect to the exam.
    if (storedInvite?.invitedToken === token && storedInvite?.testSetId) {
      this.router.navigate(['/exam', storedInvite.testSetId]);
    }
  }

  async handleConfirmEmail(event: Event) {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = formData.get('email') as string;

    const storedInvite = this.getStoredInviteData();
    const token = this.urlToken ?? storedInvite?.invitedToken;

    if (!token) {
      this.showToast('error', 'No invitation token found.');
      return;
    }

    // If valid data already exists, redirect without calling the API again.
    if (storedInvite?.invitedToken === token && storedInvite?.testSetId) {
      this.router.navigate(['/exam', storedInvite.testSetId]);
      return;
    }

    try {
      this.disabled.set(true);
      const body = { invitedEmail: email, invitedToken: token };
      const res = (await this.apiClient.checkEmail(body)) as CheckEmailResponse;

      if (res?.data?.testSetId) {
        const newInviteData = {
          testSetId: res.data.testSetId,
          expiredAt: res.data.expiredAt,
          email,
          invitedToken: token,
        };

        localStorage.setItem(
          'inviteData',
          JSON.stringify({ data: newInviteData })
        );
        localStorage.removeItem('eachProblemResult');

        // Track successful sign in
        this.analytics.trackUserSignIn(email);

        this.router.navigate(['/exam', newInviteData.testSetId]);
      } else {
        this.showToast(
          'error',
          'Could not verify email. API response was invalid.'
        );
      }
      this.enableButton();
    } catch (error) {
      console.error('Error during email confirmation:', error);
      if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        error.status === 410
      ) {
        this.showToast(
          'error',
          'This test has ended. Please contact the test creator for more information.'
        );
      } else {
        this.showToast(
          'error',
          'An error occurred. Please check the email and try again.'
        );
      }
      this.enableButton();
    }
  }

  private getStoredInviteData(): InviteData['data'] | null {
    const stored = localStorage.getItem('inviteData');
    if (!stored) {
      return null;
    }

    try {
      const parsed: InviteData = JSON.parse(stored);
      return parsed.data;
    } catch (error) {
      console.warn('Failed to parse inviteData from localStorage:', error);
      return null;
    }
  }

  private enableButton() {
    setTimeout(() => {
      this.disabled.set(false);
    }, 3000);
  }
}
